import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getOnboarding, patchOnboarding, shareOnboarding } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import OnboardingCanvasStage from "@/components/onboarding/OnboardingCanvasStage";
import {
  WIDGET_PALETTE,
  createWidget,
  migrateSlide,
  newSlide,
  findWidgetById,
  updateWidgetById,
  deleteWidgetFromTree,
  moveWidgetEarlierInTree,
  moveWidgetLaterInTree,
  sortWidgetsByZ,
  reorderRootWidgetsByOrderedIds,
  widgetSupportsTextTypography,
  typoPresetForWidgetType,
  onboardingTextColorFromWidget,
  cloneSlideDeep,
  getSlideLayout,
  clampWidgetPaddingUnit,
  readWidgetSizeForPreview,
  mapWidgetSizePatchForPreview,
  clampWidgetMarginUnit,
  clampImageBorderWidthPx,
  clampImageShadowOffsetPx,
  normalizedImageBoxShape,
  imageSquareNormalizedHFromW,
  normalizedSlideEnterEffect,
  normalizedFireworksAsciiColor,
  normalizedFontSizeLevel,
} from "@/lib/onboardingCanvas";
import EmojiPickerPopover from "@/components/onboarding/EmojiPickerPopover";
import { appendWidgetToColumn, parseColumnsRow, setColumnsRowColumnCount } from "@/lib/onboardingColumnsRow";
import { cn } from "@/lib/utils";
import { chipsToText, newChipId, parseChipsItems, CHIP_ICON_OPTIONS, ChipIcon } from "@/lib/onboardingChips";
import { compressImageFile, readChipAttachmentAsDataUrl } from "@/lib/compressImageFile";
import {
  Loader2,
  ArrowLeft,
  Plus,
  Copy,
  Link2,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Heading2,
  Type,
  List,
  Minus,
  Square,
  StickyNote,
  Video,
  Instagram,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  Image as ImageIcon,
  Paperclip,
  LayoutGrid,
  Monitor,
  Smartphone,
  LayoutTemplate,
  SlidersHorizontal,
  ArrowDownUp,
  Palette,
  Boxes,
  Smile,
  ChevronLeft,
  ChevronRight,
  X,
  Undo2,
  Redo2,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { toast } from "sonner";

function OnboardingChipsEditor({ text, onText }) {
  const items = parseChipsItems(text);
  const push = (next) => onText(chipsToText(next));
  return (
    <div className="space-y-2">
      {items.map((it, idx) => (
        <div key={it.id || `idx-${idx}`} className="flex flex-col gap-1.5 rounded-md border border-neutral-200 bg-neutral-50/50 p-2">
          <div className="flex gap-1.5 items-center">
            {/* Selector de icono */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-neutral-500"
                  title="Elegir icono"
                >
                  {it.icon ? (
                    <ChipIcon iconId={it.icon} size={14} />
                  ) : (
                    <Smile className="w-3.5 h-3.5" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <p className="text-[10px] text-neutral-500 mb-1.5 font-medium">Icono del chip</p>
                <div className="grid grid-cols-6 gap-0.5">
                  {CHIP_ICON_OPTIONS.map((opt) => (
                    <Tooltip key={opt.id}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            "h-8 w-8 flex items-center justify-center rounded hover:bg-violet-50 transition-colors text-neutral-700",
                            it.icon === opt.id ? "bg-violet-100 ring-1 ring-violet-400" : ""
                          )}
                          onClick={() => {
                            const n = [...items];
                            n[idx] = { ...it, icon: opt.id };
                            push(n);
                          }}
                        >
                          {opt.id ? (
                            <ChipIcon iconId={opt.id} size={14} />
                          ) : (
                            <span className="text-[10px] text-neutral-400 leading-none">—</span>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-[10px]">
                        {opt.label}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Input
              className="h-7 text-xs flex-1"
              value={it.label || ""}
              onChange={(e) => {
                const n = [...items];
                n[idx] = { ...it, label: e.target.value };
                push(n);
              }}
              placeholder="Texto del chip"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-red-500 hover:text-red-600"
              onClick={() => push(items.filter((_, i) => i !== idx))}
              aria-label="Quitar chip"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          <Input
            className="h-8 text-xs font-mono"
            value={it.url || ""}
            onChange={(e) => {
              const n = [...items];
              n[idx] = { ...it, url: e.target.value, kind: "url" };
              push(n);
            }}
            placeholder="https://…"
          />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-8 text-xs"
          onClick={() => push([...items, { id: newChipId(), kind: "url", label: "", url: "", mime: "", icon: "" }])}
        >
          + Enlace
        </Button>
        <label className="inline-flex cursor-pointer items-center justify-center rounded-md border border-neutral-300 bg-white px-3 text-xs font-medium hover:bg-neutral-50 h-8">
          + Archivo
          <input
            type="file"
            className="sr-only"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              try {
                const url = await readChipAttachmentAsDataUrl(f);
                push([...items, { id: newChipId(), kind: "file", label: f.name || "Archivo", url, mime: f.type || "", icon: "" }]);
              } catch (_) {
                toast.error("No se pudo leer el archivo");
              }
            }}
          />
        </label>
      </div>
    </div>
  );
}

const ICONS = {
  heading: Heading2,
  text: Type,
  bullets: List,
  divider: Minus,
  shape: Square,
  sticky: StickyNote,
  embed_tiktok: Video,
  embed_instagram: Instagram,
  image: ImageIcon,
  chips: Paperclip,
  columns_row: LayoutGrid,
  emoji: Smile,
};

const THUMB_W = 112;
const THUMB_SCALE = THUMB_W / 960;

function typographySizeSliderEnds(type) {
  if (type === "heading") return { left: "H6", right: "H1" };
  if (type === "emoji") return { left: "H6 (pequeño)", right: "H1 (grande)" };
  return { left: "Pequeño", right: "Grande" };
}

function typographySizeLabel(type) {
  const m = {
    heading: "Nivel de título",
    text: "Tamaño del texto",
    bullets: "Tamaño de la lista",
    sticky: "Tamaño del texto",
    image: "Tamaño de la leyenda",
    chips: "Tamaño del texto (enlaces)",
    emoji: "Tamaño del emoji",
  };
  return m[type] || "Tamaño del texto";
}

function typographyColorLabel(type) {
  const m = {
    heading: "Color del título",
    text: "Color del texto",
    bullets: "Color del texto",
    sticky: "Color del texto",
    image: "Color de la leyenda",
    chips: "Color del texto (enlaces)",
  };
  return m[type] || "Color del texto";
}

function RootWidgetOrderStrip({ widgets, onReorder }) {
  const sorted = useMemo(() => sortWidgetsByZ(widgets || []), [widgets]);
  const onDragStart = (e, id) => {
    e.dataTransfer.setData("text/ob-wid", id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    const src = e.dataTransfer.getData("text/ob-wid");
    if (!src || src === targetId) return;
    const ids = sorted.map((w) => w.id);
    const from = ids.indexOf(src);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, src);
    onReorder(next);
  };
  const snippet = (w) => {
    if (w.type === "chips") {
      const n = parseChipsItems(w.text || "").length;
      return n ? `${n} enlace(s)` : "—";
    }
    if (w.type === "emoji") return w.text || "—";
    if (String(w.type || "").startsWith("embed_")) return (w.text || "").trim().slice(0, 18) || "—";
    return (w.text || "").replace(/\s+/g, " ").trim().slice(0, 20) || "—";
  };

  return (
    <ul className="space-y-1">
      {sorted.map((w) => {
        const label = WIDGET_PALETTE.find((p) => p.type === w.type)?.label || w.type;
        const isColumns = w.type === "columns_row";
        const colsData = isColumns ? (() => { try { return parseColumnsRow(w.text); } catch { return null; } })() : null;
        return (
          <li key={w.id}>
            <div
              draggable={!isColumns}
              onDragStart={!isColumns ? (e) => onDragStart(e, w.id) : undefined}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, w.id)}
              className="flex items-center gap-2 rounded-md border border-neutral-100 bg-neutral-50/90 px-2 py-1.5 text-[11px] text-neutral-700"
            >
              <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-neutral-400" aria-hidden />
              <span className="min-w-0 shrink truncate font-medium">{label}</span>
              {!isColumns && (
                <span className="min-w-0 shrink text-[10px] text-neutral-400 truncate text-right">{snippet(w)}</span>
              )}
            </div>
            {/* Sub-items para columns_row: muestra cada columna y sus widgets */}
            {isColumns && colsData && (
              <ul className="mt-0.5 ml-4 space-y-0.5">
                {colsData.cells.map((col, ci) => (
                  <li key={`${w.id}-col-${ci}`}>
                    <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] text-neutral-500 font-semibold uppercase tracking-wide">
                      <LayoutGrid className="h-3 w-3 shrink-0" />
                      Columna {ci + 1}
                      <span className="font-normal normal-case tracking-normal text-neutral-400">({col.length} widget{col.length !== 1 ? "s" : ""})</span>
                    </div>
                    {col.length > 0 && (
                      <ul className="ml-3 space-y-0.5">
                        {col.map((cw) => {
                          const cwLabel = WIDGET_PALETTE.find((p) => p.type === cw.type)?.label || cw.type;
                          return (
                            <li
                              key={cw.id}
                              className="flex items-center gap-1.5 rounded border border-neutral-100 bg-white px-2 py-1 text-[10px] text-neutral-600"
                            >
                              <span className="font-medium truncate">{cwLabel}</span>
                              <span className="text-neutral-400 truncate">{snippet(cw)}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function OnboardingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const { setOnboardingPreview } = useOutletContext() || {};

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [titulo, setTitulo] = useState("");

  // ── Historia para Undo / Redo ──────────────────────────────────────────────
  const [slides, setSlidesRaw] = useState([]);
  const slidesRef = useRef([]);
  const historyRef = useRef([]);
  const futureRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  useEffect(() => { slidesRef.current = slides; }, [slides]);

  /** Cambia el estado de slides guardando la instantánea anterior en el historial. */
  const setSlides = useCallback((updater) => {
    const snapshot = slidesRef.current;
    const next = typeof updater === "function" ? updater(snapshot) : updater;
    historyRef.current = [...historyRef.current.slice(-49), snapshot];
    futureRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
    setSlidesRaw(next);
  }, []);

  const undo = useCallback(() => {
    if (!historyRef.current.length) return;
    const prev = historyRef.current[historyRef.current.length - 1];
    futureRef.current = [slidesRef.current, ...futureRef.current.slice(0, 49)];
    historyRef.current = historyRef.current.slice(0, -1);
    setCanUndo(historyRef.current.length > 0);
    setCanRedo(true);
    setSlidesRaw(prev);
  }, []);

  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    const next = futureRef.current[0];
    historyRef.current = [...historyRef.current.slice(-49), slidesRef.current];
    futureRef.current = futureRef.current.slice(1);
    setCanUndo(true);
    setCanRedo(futureRef.current.length > 0);
    setSlidesRaw(next);
  }, []);
  // ──────────────────────────────────────────────────────────────────────────
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedWidgetIds, setSelectedWidgetIds] = useState([]);
  const [shareUrl, setShareUrl] = useState("");
  const [sharePassword, setSharePassword] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareSlug, setShareSlug] = useState("");
  const [slugSaving, setSlugSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewViewport, setPreviewViewport] = useState("desktop");
  const [previewIndex, setPreviewIndex] = useState(0);
  /** Lienzo principal: escritorio 16:9 o responsive siempre vertical (9:16). */
  const [editorDevicePreview, setEditorDevicePreview] = useState("desktop");
  /** Panel derecho escritorio: null = solo iconos; cada clave expande una sección. */
  const [rightPanelSection, setRightPanelSection] = useState(null);
  /** Sidebar izquierdo de paleta de widgets visible/oculto. */
  const [paletteOpen, setPaletteOpen] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.onboarding === false) {
      if (user.permissions?.candidatos !== false) navigate("/candidatos", { replace: true });
      else navigate("/", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const doc = await getOnboarding(id);
      const t = doc.titulo ?? "";
      const raw = Array.isArray(doc.slides) ? doc.slides : [];
      const migrated = raw.length ? raw.map(migrateSlide) : [newSlide()];
      setTitulo(t);
      historyRef.current = [];
      futureRef.current = [];
      setCanUndo(false);
      setCanRedo(false);
      setSlidesRaw(migrated);
      setActiveIndex(0);
      setSelectedWidgetIds([]);
      const slug = (doc.share_slug || "").trim();
      setShareSlug(slug);
      setShareUrl(slug ? `${window.location.origin}/ob/${slug}` : "");
      setSharePassword(doc.share_password || "");
      setOnboardingPreview?.(id, { titulo: t });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar");
      navigate("/onboarding", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, setOnboardingPreview]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (id) setOnboardingPreview?.(id, null);
    };
  }, [id, setOnboardingPreview]);

  const persistPreview = useCallback(
    (nextTitulo) => {
      if (!id) return;
      setOnboardingPreview?.(id, { titulo: nextTitulo });
    },
    [id, setOnboardingPreview]
  );

  const safeIndex = useMemo(() => {
    if (!slides.length) return 0;
    return Math.min(Math.max(0, activeIndex), slides.length - 1);
  }, [slides.length, activeIndex]);

  const currentSlide = slides[safeIndex] || null;
  const widgets = currentSlide?.widgets || [];

  const primarySelectedId = useMemo(() => {
    if (!selectedWidgetIds.length) return null;
    return selectedWidgetIds[selectedWidgetIds.length - 1];
  }, [selectedWidgetIds]);

  const handleSelectWidget = useCallback((id, opts) => {
    if (opts?.prune?.length) {
      setSelectedWidgetIds((prev) => prev.filter((x) => !opts.prune.includes(x)));
      return;
    }
    if (id == null) {
      setSelectedWidgetIds([]);
      return;
    }
    const shift = Boolean(opts?.shiftKey);
    setSelectedWidgetIds((prev) => {
      if (shift) {
        if (prev.includes(id)) return prev.filter((x) => x !== id);
        return [...prev, id];
      }
      return [id];
    });
  }, []);

  const updateCurrentWidgets = useCallback(
    (updater) => {
      setSlides((prev) =>
        prev.map((s, i) => {
          if (i !== safeIndex) return s;
          const nextW = typeof updater === "function" ? updater(s.widgets || []) : updater;
          return { ...s, widgets: nextW };
        })
      );
    },
    [safeIndex]
  );

  const patchSelectedWidget = useCallback(
    (partial) => {
      if (!primarySelectedId) return;
      const mapped = mapWidgetSizePatchForPreview(partial, editorDevicePreview === "responsive" ? "responsive" : undefined);
      updateCurrentWidgets((list) => updateWidgetById(list, primarySelectedId, mapped));
    },
    [primarySelectedId, updateCurrentWidgets, editorDevicePreview]
  );

  const handleSave = async () => {
    if (!id || saving) return;
    setSaving(true);
    try {
      await patchOnboarding(id, { titulo, slides });
      toast.success("Guardado");
      persistPreview(titulo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const addSlide = () => {
    setSlides((prev) => {
      const next = [...prev, newSlide()];
      setActiveIndex(next.length - 1);
      return next;
    });
    setSelectedWidgetIds([]);
  };

  const removeSlide = (index) => {
    setSlides((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      return next;
    });
    setActiveIndex((i) => {
      if (index < i) return i - 1;
      if (index === i) return Math.max(0, i - 1);
      return i;
    });
    setSelectedWidgetIds([]);
  };

  const duplicateSlide = (index) => {
    setSlides((prev) => {
      const s = prev[index];
      if (!s) return prev;
      const copy = cloneSlideDeep(s);
      setActiveIndex(index + 1);
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
    setSelectedWidgetIds([]);
    toast.success("Diapositiva duplicada");
  };

  const addWidget = (type) => {
    const w = createWidget(type);
    const rowId = [...selectedWidgetIds].reverse().find((sid) => findWidgetById(widgets, sid)?.type === "columns_row");
    const sel = rowId ? findWidgetById(widgets, rowId) : null;
    if (sel?.type === "columns_row" && rowId) {
      updateCurrentWidgets((list) => {
        const row = findWidgetById(list, rowId);
        if (!row || row.type !== "columns_row") return [...list, w];
        return updateWidgetById(list, rowId, { text: appendWidgetToColumn(row.text, 0, w) });
      });
      setSelectedWidgetIds([w.id]);
      return;
    }
    updateCurrentWidgets((list) => [...list, w]);
    setSelectedWidgetIds([w.id]);
  };

  const deleteSelectedWidget = useCallback(() => {
    if (!selectedWidgetIds.length) return;
    updateCurrentWidgets((list) => selectedWidgetIds.reduce((acc, wid) => deleteWidgetFromTree(acc, wid), list));
    setSelectedWidgetIds([]);
  }, [selectedWidgetIds, updateCurrentWidgets]);

  const moveWidgetUp = useCallback(() => {
    if (!selectedWidgetIds.length) return;
    updateCurrentWidgets((list) =>
      [...selectedWidgetIds].reverse().reduce((acc, wid) => moveWidgetEarlierInTree(acc, wid), list)
    );
  }, [selectedWidgetIds, updateCurrentWidgets]);

  const moveWidgetDown = useCallback(() => {
    if (!selectedWidgetIds.length) return;
    updateCurrentWidgets((list) => selectedWidgetIds.reduce((acc, wid) => moveWidgetLaterInTree(acc, wid), list));
  }, [selectedWidgetIds, updateCurrentWidgets]);

  const reorderRootFromDrag = useCallback(
    (orderedIds) => {
      updateCurrentWidgets((list) => reorderRootWidgetsByOrderedIds(list, orderedIds));
    },
    [updateCurrentWidgets]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        const t = e.target;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
        deleteSelectedWidget();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteSelectedWidget]);

  // Ctrl+Z → undo; Ctrl+Y / Ctrl+Shift+Z → redo
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (e.key === "y" || (e.key === "z" && e.shiftKey)) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const handleShare = async () => {
    if (!id || sharing) return;
    setSharing(true);
    try {
      const data = await shareOnboarding(id);
      let url = data.share_url || "";
      if (url.startsWith("/")) {
        url = `${window.location.origin}${url}`;
      }
      setShareUrl(url);
      setSharePassword(data.password || "");
      if (data.share_slug) setShareSlug(data.share_slug);
      toast.success("Enlace listo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar enlace");
    } finally {
      setSharing(false);
    }
  };

  const saveShareSlug = async () => {
    if (!id || slugSaving) return;
    const raw = shareSlug.trim();
    if (!raw) {
      toast.error("Indica un slug");
      return;
    }
    setSlugSaving(true);
    try {
      const doc = await patchOnboarding(id, { share_slug: raw });
      const next = (doc.share_slug || raw).trim();
      setShareSlug(next);
      setShareUrl(`${window.location.origin}/ob/${next}`);
      toast.success("Slug guardado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar el slug");
    } finally {
      setSlugSaving(false);
    }
  };

  const copyText = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch (_) {
      toast.error("No se pudo copiar");
    }
  };

  const selectedWidget = primarySelectedId ? findWidgetById(widgets, primarySelectedId) : null;
  const selectedWidgetSize = useMemo(
    () => readWidgetSizeForPreview(selectedWidget, editorDevicePreview === "responsive" ? "responsive" : undefined),
    [selectedWidget, editorDevicePreview]
  );
  const slideLayoutUi = useMemo(() => getSlideLayout(currentSlide || {}), [
    currentSlide?.layoutColumns,
    currentSlide?.widgetGapRem,
    currentSlide?.widgetMaxWidthPx,
    currentSlide?.canvasPadDesktopPx,
    currentSlide?.canvasPadMobilePx,
  ]);

  if (authLoading || !user || user.permissions?.onboarding === false) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-col h-full min-h-0 bg-neutral-100/80 text-neutral-900">
        <header className="shrink-0 flex flex-wrap items-center gap-2 px-3 py-4 border-b border-neutral-200/90 bg-white/95 backdrop-blur-sm z-20">
          <Button variant="ghost" size="icon" className="shrink-0 rounded-full" onClick={() => navigate("/onboarding")} aria-label="Volver">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <Input
            className="max-w-[min(100%,20rem)] border-0 bg-transparent shadow-none focus-visible:ring-0 text-base font-medium placeholder:text-neutral-400 px-1"
            value={titulo}
            onChange={(e) => {
              const v = e.target.value;
              setTitulo(v);
              persistPreview(v);
            }}
            placeholder="Sin título"
          />
          <div className="flex-1" />
          {/* Deshacer / Rehacer */}
          <div className="flex items-center gap-0.5 rounded-full border border-neutral-200/90 bg-neutral-50/90 p-0.5 shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  aria-label="Deshacer"
                  disabled={!canUndo}
                  onClick={undo}
                >
                  <Undo2 className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Deshacer (Ctrl+Z)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  aria-label="Rehacer"
                  disabled={!canRedo}
                  onClick={redo}
                >
                  <Redo2 className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Rehacer (Ctrl+Y)</TooltipContent>
            </Tooltip>
          </div>
          {/* Selector de dispositivo */}
          <div className="flex items-center gap-0.5 rounded-full border border-neutral-200/90 bg-neutral-50/90 p-0.5 shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={editorDevicePreview === "desktop" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  aria-pressed={editorDevicePreview === "desktop"}
                  aria-label="Vista escritorio en el lienzo"
                  onClick={() => setEditorDevicePreview("desktop")}
                >
                  <Monitor className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs max-w-[14rem]">
                Lienzo escritorio (16:9). Columnas según la diapositiva.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={editorDevicePreview === "responsive" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  aria-pressed={editorDevicePreview === "responsive"}
                  aria-label="Vista móvil vertical en el lienzo"
                  onClick={() => setEditorDevicePreview("responsive")}
                >
                  <Smartphone className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs max-w-[14rem]">
                Lienzo móvil vertical (9:16), una sola columna y márgenes móviles para ajustar widgets.
              </TooltipContent>
            </Tooltip>
          </div>
          <Button type="button" variant="outline" size="sm" className="gap-1.5 rounded-full border-neutral-200" onClick={() => { setPreviewIndex(safeIndex); setPreviewOpen(true); }}>
            <Monitor className="w-4 h-4" strokeWidth={1.5} />
            Previsualizar
          </Button>
          <Popover open={shareOpen} onOpenChange={setShareOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 rounded-full border-neutral-200">
                <Link2 className="w-4 h-4" strokeWidth={1.5} />
                Compartir
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[min(100vw-1.5rem,22rem)]" align="end">
              <p className="text-sm text-neutral-600 mb-3">URL pública personalizada y contraseña.</p>
              <div className="space-y-2 mb-3">
                <label className="text-xs text-neutral-500">Slug (ruta /ob/…)</label>
                <div className="flex gap-1.5">
                  <Input
                    className="h-9 text-sm font-mono"
                    value={shareSlug}
                    onChange={(e) => setShareSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    placeholder="mi-bienvenida"
                    spellCheck={false}
                  />
                  <Button type="button" size="sm" variant="secondary" className="shrink-0 h-9 px-2" onClick={saveShareSlug} disabled={slugSaving}>
                    {slugSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "OK"}
                  </Button>
                </div>
              </div>
              {!sharePassword && (
                <Button type="button" size="sm" className="w-full gap-2 mb-3" onClick={handleShare} disabled={sharing}>
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
                  Obtener contraseña
                </Button>
              )}
              {shareUrl ? (
                <div className="space-y-2 text-xs">
                  <div className="flex gap-2 items-start">
                    <code className="flex-1 break-all bg-neutral-50 rounded p-2 border">{shareUrl}</code>
                    <Button type="button" variant="outline" size="icon" className="shrink-0 h-8 w-8" onClick={() => copyText(shareUrl, "URL")}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {sharePassword ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-neutral-500">Clave</span>
                      <code className="font-semibold tracking-widest">{sharePassword}</code>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => copyText(sharePassword, "Contraseña")}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </PopoverContent>
          </Popover>
          <Button size="sm" className="rounded-full gap-2 px-4" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
            Guardar
          </Button>
        </header>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar izquierdo: paleta de widgets + toggle */}
          <aside
            className={cn(
              "shrink-0 border-r border-neutral-200/90 bg-white flex flex-col items-center z-10 transition-[width] duration-200 overflow-hidden",
              paletteOpen ? "w-14 sm:w-[4.5rem]" : "w-10"
            )}
          >
            {/* Botón toggle siempre visible */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-2 h-8 w-8 rounded-lg text-neutral-500 hover:text-violet-700 hover:bg-violet-50 shrink-0"
                  onClick={() => setPaletteOpen((v) => !v)}
                  aria-label={paletteOpen ? "Ocultar paleta" : "Mostrar paleta"}
                >
                  {paletteOpen
                    ? <PanelLeftClose className="w-4 h-4" strokeWidth={1.5} />
                    : <PanelLeftOpen className="w-4 h-4" strokeWidth={1.5} />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">
                {paletteOpen ? "Ocultar paleta" : "Mostrar paleta"}
              </TooltipContent>
            </Tooltip>
            {paletteOpen && <div className="w-8 border-t border-neutral-200/80 my-1" />}
            {/* Widgets de la paleta, visibles solo cuando está abierto */}
            {paletteOpen && (
              <div className="flex flex-col items-center gap-1.5 pb-3">
                {WIDGET_PALETTE.map(({ type, label }) => {
                  const Icon = ICONS[type] || Square;
                  return (
                    <Tooltip key={type}>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="rounded-xl h-11 w-11 text-neutral-600 hover:text-violet-700 hover:bg-violet-50"
                          onClick={() => addWidget(type)}
                          aria-label={label}
                        >
                          <Icon className="w-5 h-5" strokeWidth={1.5} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="text-xs">
                        {label}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </aside>

          <div className="flex-1 flex min-w-0 min-h-0">
            <main className="flex-1 flex flex-col min-w-0 min-h-0 p-3 sm:p-4 gap-3">
              <div className="flex-1 flex items-start justify-center min-h-0 overflow-y-auto overflow-x-hidden w-full">
                <div
                  className={cn(
                    "w-full shrink-0 my-1 sm:my-2 ob-slide-fade",
                    editorDevicePreview === "responsive"
                      ? "max-w-[min(100%,390px)] [aspect-ratio:9/16]"
                      : "max-w-[960px] h-[max(72vh,520px)]"
                  )}
                  key={currentSlide?.id}
                >
                  <OnboardingCanvasStage
                    widgets={widgets}
                    layoutSlide={currentSlide}
                    background={currentSlide?.background || "#ffffff"}
                    readOnly={false}
                    animated={false}
                    selectedIds={selectedWidgetIds}
                    onSelectWidget={handleSelectWidget}
                    onWidgetsChange={updateCurrentWidgets}
                    fullscreen
                    className="w-full h-full min-h-0"
                    previewDevice={editorDevicePreview === "responsive" ? "responsive" : undefined}
                  />
                </div>
              </div>

              <div className="shrink-0 flex items-center gap-2 overflow-x-auto pb-1 pt-1 px-0.5">
                {slides.map((slide, idx) => (
                  <div key={slide.id} className="flex flex-col items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveIndex(idx);
                        setSelectedWidgetIds([]);
                      }}
                      className={`relative rounded-lg overflow-hidden border-2 transition-colors bg-neutral-200/50 ${idx === safeIndex ? "border-violet-500 ring-2 ring-violet-200" : "border-transparent hover:border-neutral-300"}`}
                      style={{ width: THUMB_W, height: THUMB_W * (540 / 960) }}
                      aria-label={`Diapositiva ${idx + 1}`}
                    >
                      <div
                        className="pointer-events-none absolute left-0 top-0 origin-top-left bg-white"
                        style={{
                          width: 960,
                          height: 540,
                          transform: `scale(${THUMB_SCALE})`,
                        }}
                      >
                        <OnboardingCanvasStage
                          widgets={slide.widgets || []}
                          layoutSlide={slide}
                          background={slide.background || "#ffffff"}
                          readOnly
                          className="w-full h-full !rounded-none !shadow-none"
                        />
                      </div>
                      {/* Capa invisible que bloquea cualquier evento de los iframes (embed) dentro de la miniatura */}
                      <div className="absolute inset-0 z-[200]" aria-hidden />
                    </button>
                    <span className="text-[10px] tabular-nums text-neutral-500">{idx + 1}</span>
                  </div>
                ))}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="outline" size="icon" className="shrink-0 rounded-lg h-[calc(112*9/16+4px)] w-10 border-dashed" onClick={addSlide} aria-label="Nueva diapositiva">
                      <Plus className="w-5 h-5" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Nueva diapositiva</TooltipContent>
                </Tooltip>
              </div>

              <div className="lg:hidden border border-neutral-200 rounded-xl bg-white p-3 space-y-2 shrink-0 max-h-[40vh] overflow-y-auto">
                <p className="text-xs font-medium text-neutral-500">Edición rápida</p>
                {widgets.length > 0 ? (
                  <div className="space-y-1 border-b border-neutral-100 pb-2">
                    <span className="text-[10px] text-neutral-500">Orden (arrastra)</span>
                    <RootWidgetOrderStrip widgets={widgets} onReorder={reorderRootFromDrag} />
                  </div>
                ) : null}
                {!selectedWidget ? (
                  <p className="text-xs text-neutral-400">Selecciona un elemento en el lienzo.</p>
                ) : (
                  <>
                    {(selectedWidget.type === "heading" ||
                      selectedWidget.type === "text" ||
                      selectedWidget.type === "bullets" ||
                      selectedWidget.type === "sticky" ||
                      selectedWidget.type === "chips") && (
                      <div className="flex gap-1 flex-wrap">
                        {["left", "center", "right", ...(selectedWidget.type !== "chips" ? ["justify"] : [])].map((k) => (
                          <Button
                            key={k}
                            type="button"
                            variant={selectedWidgetSize.textAlign === k ? "default" : "outline"}
                            size="sm"
                            className="h-8 px-2 text-[10px] capitalize"
                            onClick={() => patchSelectedWidget({ textAlign: k })}
                          >
                            {k === "justify" ? "Justif." : k.slice(0, 3)}
                          </Button>
                        ))}
                        {selectedWidget.type !== "chips" && ["start", "center", "end"].map((k) => (
                          <Button
                            key={`v-${k}`}
                            type="button"
                            variant={selectedWidgetSize.verticalAlign === k ? "default" : "outline"}
                            size="sm"
                            className="h-8 px-2 text-[10px]"
                            onClick={() => patchSelectedWidget({ verticalAlign: k })}
                          >
                            {k}
                          </Button>
                        ))}
                      </div>
                    )}
                    {selectedWidget.type === "emoji" && (
                      <div className="space-y-2 text-xs border-t border-neutral-100 pt-2 mt-1">
                        <span className="font-medium text-neutral-500 block">Emoji</span>
                        <div className="flex items-center gap-2">
                          <EmojiPickerPopover
                            value={selectedWidget.text}
                            onSelect={(e) => patchSelectedWidget({ text: e })}
                          >
                            <Button type="button" variant="outline" className="h-10 px-3 text-2xl">
                              {selectedWidget.text || "🎉"}
                            </Button>
                          </EmojiPickerPopover>
                          <span className="text-neutral-400 text-[10px]">Pulsa para cambiar</span>
                        </div>
                      </div>
                    )}
                    {widgetSupportsTextTypography(selectedWidget.type) && (
                      <div className="space-y-2 text-xs border-t border-neutral-100 pt-2 mt-1">
                        <span className="font-medium text-neutral-500 block">{typographySizeLabel(selectedWidget.type)}</span>
                        <Slider
                          value={[Math.max(1, Math.min(5, Number(selectedWidgetSize.fontSizeLevel) || 3))]}
                          min={1}
                          max={5}
                          step={1}
                          onValueChange={([v]) => patchSelectedWidget({ fontSizeLevel: v })}
                        />
                        <div className="flex justify-between text-[10px] text-neutral-400 px-0.5 tabular-nums">
                          <span>{typographySizeSliderEnds(selectedWidget.type).left}</span>
                          <span>{typographySizeSliderEnds(selectedWidget.type).right}</span>
                        </div>
                        {selectedWidget.type !== "emoji" && (
                          <>
                            <span className="font-medium text-neutral-500 block">{typographyColorLabel(selectedWidget.type)}</span>
                            <div className="flex gap-2 items-center">
                              <input
                                type="color"
                                className="h-8 w-10 shrink-0 cursor-pointer rounded border border-neutral-200 bg-white p-0.5"
                                aria-label="Color del texto"
                                value={
                                  /^#[0-9a-fA-F]{6}$/.test(String(selectedWidget.textColor || "").trim())
                                    ? String(selectedWidget.textColor).trim()
                                    : onboardingTextColorFromWidget({}, typoPresetForWidgetType(selectedWidget.type))
                                }
                                onChange={(e) => patchSelectedWidget({ textColor: e.target.value })}
                              />
                              <Input
                                className="h-8 text-[11px] font-mono flex-1"
                                value={selectedWidget.textColor || ""}
                                onChange={(e) => patchSelectedWidget({ textColor: e.target.value })}
                                placeholder={onboardingTextColorFromWidget({}, typoPresetForWidgetType(selectedWidget.type))}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {String(selectedWidget.type || "").startsWith("embed_") ? (
                      <textarea
                        className="w-full min-h-[72px] text-xs rounded-md border border-neutral-200 p-2 font-mono"
                        value={selectedWidget.text || ""}
                        onChange={(e) => patchSelectedWidget({ text: e.target.value })}
                        placeholder="URL de vídeo, publicación o perfil"
                      />
                    ) : null}
                    {selectedWidget.type === "image" ? (
                      <div className="space-y-2 text-xs">
                        <Input
                          className="h-8 text-xs font-mono"
                          value={selectedWidget.imageSrc || ""}
                          onChange={(e) => patchSelectedWidget({ imageSrc: e.target.value })}
                          placeholder="URL imagen"
                        />
                        <label className="flex cursor-pointer items-center justify-center rounded border px-2 py-1.5">
                          Subir imagen
                          <input
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              e.target.value = "";
                              if (!f) return;
                              try {
                                patchSelectedWidget({ imageSrc: await compressImageFile(f) });
                              } catch (_) {
                                toast.error("Error al leer imagen");
                              }
                            }}
                          />
                        </label>
                        <Input
                          className="h-8 text-xs"
                          value={selectedWidget.text || ""}
                          onChange={(e) => patchSelectedWidget({ text: e.target.value })}
                          placeholder="Leyenda"
                        />
                        <div className="flex gap-1 flex-wrap">
                          {[
                            { k: "rect", label: "Rectangular" },
                            { k: "square", label: "Cuadrada" },
                            { k: "circle", label: "Circular" },
                          ].map(({ k, label }) => (
                            <Button
                              key={k}
                              type="button"
                              size="sm"
                              variant={normalizedImageBoxShape(selectedWidget.imageBoxShape) === k ? "default" : "outline"}
                              className="h-7 text-[10px] flex-1 min-w-[4.5rem]"
                              onClick={() => {
                                const wf = Math.max(0.1, Math.min(1, Number(selectedWidgetSize.w) || 1));
                                if (k === "square" || k === "circle") {
                                  patchSelectedWidget({ imageBoxShape: k, h: imageSquareNormalizedHFromW(wf) });
                                } else {
                                  patchSelectedWidget({ imageBoxShape: "rect" });
                                }
                              }}
                            >
                              {label}
                            </Button>
                          ))}
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!selectedWidget.lockAspectRatio}
                            onChange={(e) => patchSelectedWidget({ lockAspectRatio: e.target.checked })}
                            className="rounded"
                          />
                          Mantener proporción al redimensionar (no deformar la imagen)
                        </label>
                        {normalizedImageBoxShape(selectedWidget.imageBoxShape) !== "circle" ? (
                          <div className="space-y-1">
                            <div className="flex justify-between text-[10px] text-neutral-500">
                              <span>Redondeo bordes</span>
                              <span>{Math.max(0, Math.min(50, Number(selectedWidget.borderRadius) || 0))}</span>
                            </div>
                            <Slider
                              value={[Math.max(0, Math.min(50, Number(selectedWidget.borderRadius) || 0))]}
                              min={0}
                              max={50}
                              step={1}
                              onValueChange={([v]) => patchSelectedWidget({ borderRadius: v })}
                            />
                          </div>
                        ) : (
                          <p className="text-[10px] text-neutral-400">Forma circular: el marco es siempre un círculo perfecto.</p>
                        )}
                        <div className="flex gap-2 items-center pt-1">
                          <input
                            type="color"
                            className="h-7 w-8 shrink-0 cursor-pointer rounded border border-neutral-200 p-0.5"
                            value={
                              /^#[0-9a-fA-F]{6}$/.test(String(selectedWidget.imageBorderColor || "").trim())
                                ? String(selectedWidget.imageBorderColor).trim()
                                : "#d4d4d4"
                            }
                            onChange={(e) => patchSelectedWidget({ imageBorderColor: e.target.value })}
                            aria-label="Color borde"
                          />
                          <span className="text-[10px] text-neutral-500 shrink-0">Borde</span>
                          <Slider
                            className="flex-1 min-w-0"
                            value={[clampImageBorderWidthPx(selectedWidget.imageBorderWidthPx)]}
                            min={0}
                            max={16}
                            step={1}
                            onValueChange={([v]) => patchSelectedWidget({ imageBorderWidthPx: v })}
                          />
                        </div>
                        <div className="space-y-1 pt-1">
                          <span className="text-[10px] text-neutral-500">Sombra paralela (px)</span>
                          <Slider
                            value={[clampImageShadowOffsetPx(selectedWidget.imageShadowOffsetPx)]}
                            min={0}
                            max={24}
                            step={1}
                            onValueChange={([v]) => patchSelectedWidget({ imageShadowOffsetPx: v })}
                          />
                          <Input
                            className="h-7 text-[10px] font-mono"
                            value={selectedWidget.imageShadowColor || ""}
                            onChange={(e) => patchSelectedWidget({ imageShadowColor: e.target.value })}
                            placeholder="rgba(…)"
                          />
                        </div>
                      </div>
                    ) : null}
                    {selectedWidget.type === "chips" ? (
                      <OnboardingChipsEditor text={selectedWidget.text || ""} onText={(t) => patchSelectedWidget({ text: t })} />
                    ) : null}
                    {selectedWidget.type === "columns_row" ? (
                      <div className="space-y-1.5 text-xs">
                        <div className="flex gap-1">
                          {[1, 2, 3].map((n) => {
                            const cur = parseColumnsRow(selectedWidget.text || "{}").columnCount;
                            return (
                              <Button
                                key={n}
                                type="button"
                                size="sm"
                                variant={cur === n ? "default" : "outline"}
                                className="h-7 flex-1 text-[10px]"
                                onClick={() => patchSelectedWidget({ text: setColumnsRowColumnCount(selectedWidget.text || "{}", n) })}
                              >
                                {n} col.
                              </Button>
                            );
                          })}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {parseColumnsRow(selectedWidget.text || "{}").cells.map((_, colIdx) => (
                            <Button
                              key={colIdx}
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 text-[10px]"
                              onClick={() => {
                                const nw = createWidget("text");
                                updateCurrentWidgets((list) => {
                                  const row = findWidgetById(list, primarySelectedId);
                                  if (!row || row.type !== "columns_row") return list;
                                  return updateWidgetById(list, primarySelectedId, {
                                    text: appendWidgetToColumn(row.text, colIdx, nw),
                                  });
                                });
                                setSelectedWidgetIds([nw.id]);
                              }}
                            >
                              + Col {colIdx + 1}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {selectedWidget.type !== "divider" &&
                    selectedWidget.type !== "shape" &&
                    selectedWidget.type !== "heading" &&
                    selectedWidget.type !== "text" &&
                    selectedWidget.type !== "bullets" &&
                    selectedWidget.type !== "sticky" &&
                    !String(selectedWidget.type || "").startsWith("embed_") &&
                    selectedWidget.type !== "image" &&
                    selectedWidget.type !== "chips" &&
                    selectedWidget.type !== "columns_row" ? (
                      <textarea
                        className="w-full min-h-[88px] text-sm rounded-md border border-neutral-200 p-2 resize-y focus:outline-none focus:ring-2 focus:ring-violet-400"
                        value={selectedWidget.text || ""}
                        onChange={(e) => patchSelectedWidget({ text: e.target.value })}
                        placeholder="Texto…"
                      />
                    ) : null}
                    {selectedWidget.type === "divider" && (
                      <div className="space-y-2 text-xs">
                        <span className="font-medium text-neutral-500 block">Estilo de línea</span>
                        <div className="flex gap-1">
                          {[
                            { k: "solid", label: "—" },
                            { k: "dashed", label: "- -" },
                            { k: "dotted", label: "···" },
                          ].map(({ k, label }) => (
                            <Button
                              key={k}
                              type="button"
                              variant={(selectedWidget.lineStyle || "solid") === k ? "default" : "outline"}
                              size="sm"
                              className="flex-1 h-8 text-[11px] font-mono tracking-widest"
                              onClick={() => patchSelectedWidget({ lineStyle: k })}
                            >
                              {label}
                            </Button>
                          ))}
                        </div>
                        <span className="font-medium text-neutral-500 block">Color</span>
                        <input
                          type="color"
                          className="h-8 w-full rounded-md border cursor-pointer"
                          value={/^#[0-9a-fA-F]{6}$/.test(selectedWidget.accent || "") ? selectedWidget.accent : "#d4d4d4"}
                          onChange={(e) => patchSelectedWidget({ accent: e.target.value })}
                        />
                      </div>
                    )}
                    {selectedWidget.type === "shape" ? (
                      <input
                        type="color"
                        className="h-10 w-full rounded-md border cursor-pointer"
                        value={/^#[0-9a-fA-F]{6}$/.test(selectedWidget.accent || "") ? selectedWidget.accent : "#eef2ff"}
                        onChange={(e) => patchSelectedWidget({ accent: e.target.value })}
                      />
                    ) : null}
                    <div className="space-y-2 text-xs border-t border-neutral-100 pt-2 mt-1">
                      <span className="font-medium text-neutral-600 block">Padding del bloque</span>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          ["paddingTopPx", "Arriba"],
                          ["paddingBottomPx", "Abajo"],
                          ["paddingLeftPx", "Izda."],
                          ["paddingRightPx", "Dcha."],
                        ].map(([key, lab]) => (
                          <label key={key} className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-[10px] text-neutral-500">{lab}</span>
                            <Input
                              type="number"
                              min={0}
                              max={24}
                              className="h-7 text-[11px] tabular-nums"
                              value={clampWidgetPaddingUnit(selectedWidget[key])}
                              onChange={(e) => {
                                const n = Math.max(0, Math.min(24, Math.round(Number(e.target.value) || 0)));
                                patchSelectedWidget({ [key]: n });
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2 text-xs border-t border-neutral-100 pt-2 mt-1">
                      <span className="font-medium text-neutral-600 block">Margen del bloque</span>
                      <p className="text-[10px] text-neutral-400 leading-snug">Mismo paso que el padding (0,125 rem); admite valores negativos.</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          ["marginTopPx", "Arriba"],
                          ["marginBottomPx", "Abajo"],
                          ["marginLeftPx", "Izda."],
                          ["marginRightPx", "Dcha."],
                        ].map(([key, lab]) => (
                          <label key={key} className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-[10px] text-neutral-500">{lab}</span>
                            <Input
                              type="number"
                              min={-24}
                              max={24}
                              className="h-7 text-[11px] tabular-nums"
                              value={clampWidgetMarginUnit(selectedWidget[key])}
                              onChange={(e) => {
                                const n = Math.max(-24, Math.min(24, Math.round(Number(e.target.value) || 0)));
                                patchSelectedWidget({ [key]: n });
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button type="button" variant="outline" size="sm" className="flex-1 min-w-[4.5rem] text-xs h-8" onClick={moveWidgetUp}>
                        Subir
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="flex-1 min-w-[4.5rem] text-xs h-8" onClick={moveWidgetDown}>
                        Bajar
                      </Button>
                      <Button type="button" variant="destructive" size="sm" className="text-xs h-8 shrink-0" onClick={deleteSelectedWidget}>
                        Quitar
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </main>

            <aside className="hidden lg:flex min-h-0 shrink-0 flex-row border-l border-neutral-200/90 bg-white">
              <div
                className={cn(
                  "flex min-h-0 flex-col overflow-hidden border-neutral-200/80 bg-neutral-50/90 transition-[width,opacity,border-color] duration-200 ease-out",
                  rightPanelSection
                    ? "w-[min(19rem,calc(100vw-9rem))] min-w-[15rem] max-w-[22rem] border-r opacity-100"
                    : "w-0 min-w-0 max-w-0 border-transparent opacity-0 pointer-events-none"
                )}
              >
                <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3">
                  {rightPanelSection === "lienzo" && (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-neutral-900">Lienzo</p>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={() => duplicateSlide(safeIndex)}>
                          Duplicar
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs h-8 px-2 text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => removeSlide(safeIndex)}
                          disabled={slides.length <= 1}
                          aria-label="Eliminar diapositiva"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div>
                        <label className="text-xs text-neutral-500 mb-1 block">Fondo</label>
                        <input
                          type="color"
                          className="h-9 w-full rounded-md border cursor-pointer"
                          value={/^#[0-9a-fA-F]{6}$/.test(currentSlide?.background || "") ? currentSlide.background : "#ffffff"}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, background: v } : s)));
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs text-neutral-500 mb-1 block">Imagen de fondo (opcional)</label>
                        <Input
                          className="h-8 text-xs font-mono"
                          value={currentSlide?.backgroundImageSrc || ""}
                          onChange={(e) =>
                            setSlides((prev) =>
                              prev.map((s, i) => (i === safeIndex ? { ...s, backgroundImageSrc: e.target.value } : s))
                            )
                          }
                          placeholder="https://… o deja vacío"
                        />
                        <label className="inline-flex cursor-pointer items-center justify-center rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-xs hover:bg-neutral-50 w-full">
                          Subir imagen de fondo
                          <input
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              e.target.value = "";
                              if (!f) return;
                              try {
                                const u = await compressImageFile(f);
                                setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, backgroundImageSrc: u } : s)));
                              } catch (_) {
                                toast.error("No se pudo leer la imagen");
                              }
                            }}
                          />
                        </label>
                        <div className="flex gap-1">
                          {[
                            { k: "cover", label: "Cubrir" },
                            { k: "contain", label: "Contener" },
                          ].map(({ k, label }) => (
                            <Button
                              key={k}
                              type="button"
                              size="sm"
                              variant={(currentSlide?.backgroundImageFit || "cover") === k ? "default" : "outline"}
                              className="flex-1 h-8 text-xs"
                              onClick={() =>
                                setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, backgroundImageFit: k } : s)))
                              }
                            >
                              {label}
                            </Button>
                          ))}
                        </div>
                        {(currentSlide?.backgroundImageSrc || "").trim() ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-full text-xs text-neutral-600"
                            onClick={() =>
                              setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, backgroundImageSrc: "" } : s)))
                            }
                          >
                            Quitar imagen de fondo
                          </Button>
                        ) : null}
                        <p className="text-[10px] text-neutral-400 leading-snug">
                          Solo URLs https o imagen subida. El color de fondo queda visible si la imagen no llena el lienzo (p. ej. contener).
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-neutral-500 mb-1 block">Columnas del lienzo</span>
                        <div className="flex gap-1">
                          {[1, 2, 3].map((n) => (
                            <Button
                              key={n}
                              type="button"
                              size="sm"
                              variant={(currentSlide?.layoutColumns ?? 1) === n ? "default" : "outline"}
                              className="h-8 flex-1 text-xs"
                              onClick={() => setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, layoutColumns: n } : s)))}
                            >
                              {n}
                            </Button>
                          ))}
                        </div>
                        <p className="text-[10px] text-neutral-400 mt-1 leading-snug">En pantallas pequeñas se muestra una sola columna.</p>
                      </div>
                      <div className="space-y-2 border-t border-neutral-200/80 pt-3">
                        <p className="text-xs font-medium text-neutral-700">Padding del lienzo</p>
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>Escritorio</span>
                          <span className="tabular-nums">{slideLayoutUi.canvasPadDesktopPx}px</span>
                        </div>
                        <Slider
                          value={[slideLayoutUi.canvasPadDesktopPx]}
                          min={0}
                          max={64}
                          step={2}
                          onValueChange={([v]) =>
                            setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, canvasPadDesktopPx: v } : s)))
                          }
                        />
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>Móvil</span>
                          <span className="tabular-nums">{slideLayoutUi.canvasPadMobilePx}px</span>
                        </div>
                        <Slider
                          value={[slideLayoutUi.canvasPadMobilePx]}
                          min={0}
                          max={48}
                          step={2}
                          onValueChange={([v]) =>
                            setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, canvasPadMobilePx: v } : s)))
                          }
                        />
                      </div>
                      <div className="space-y-2 rounded-md border border-neutral-200/80 bg-white/80 px-2.5 py-2">
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium text-neutral-800">Efecto al entrar (vista pública)</span>
                          <p className="text-[10px] text-neutral-500 leading-snug">
                            Se reproduce al mostrar esta diapositiva en el enlace compartido (respeta “reducir movimiento” del sistema).
                          </p>
                        </div>
                        <RadioGroup
                          value={normalizedSlideEnterEffect(currentSlide || {})}
                          onValueChange={(v) =>
                            setSlides((prev) =>
                              prev.map((s, i) => {
                                if (i !== safeIndex) return s;
                                const next = { ...s, slideEnterEffect: v, confettiOnEnter: undefined };
                                if (
                                  v === "fireworks_ascii" &&
                                  !/^#[0-9a-fA-F]{6}$/.test(String(s.fireworksAsciiColor || "").trim())
                                ) {
                                  next.fireworksAsciiColor = normalizedFireworksAsciiColor({});
                                }
                                return next;
                              })
                            )
                          }
                          className="grid gap-2"
                        >
                          {[
                            { value: "none", label: "Ninguno" },
                            { value: "confetti", label: "Confetti" },
                            { value: "fireworks", label: "Fuegos (partículas)" },
                            { value: "fireworks_ascii", label: "Fuegos ASCII (firew0rks)" },
                          ].map(({ value, label }) => (
                            <div key={value} className="flex items-center gap-2">
                              <RadioGroupItem value={value} id={`ob-enter-${value}`} />
                              <Label htmlFor={`ob-enter-${value}`} className="text-xs font-normal cursor-pointer">
                                {label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                        {normalizedSlideEnterEffect(currentSlide || {}) === "fireworks_ascii" ? (
                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            <Label htmlFor="ob-fireworks-ascii-color" className="text-xs text-neutral-600 shrink-0">
                              Color fuegos ASCII
                            </Label>
                            <input
                              id="ob-fireworks-ascii-color"
                              type="color"
                              className="h-8 w-10 shrink-0 cursor-pointer rounded border border-neutral-200 bg-white p-0.5"
                              value={normalizedFireworksAsciiColor(currentSlide || {})}
                              onChange={(e) =>
                                setSlides((prev) =>
                                  prev.map((s, i) =>
                                    i === safeIndex ? { ...s, fireworksAsciiColor: e.target.value } : s
                                  )
                                )
                              }
                              aria-label="Color de la animación ASCII"
                            />
                            <Input
                              className="h-8 max-w-[7.5rem] text-[11px] font-mono"
                              value={String(currentSlide?.fireworksAsciiColor || "").trim()}
                              onChange={(e) =>
                                setSlides((prev) =>
                                  prev.map((s, i) =>
                                    i === safeIndex ? { ...s, fireworksAsciiColor: e.target.value } : s
                                  )
                                )
                              }
                              placeholder="#facc15"
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {rightPanelSection === "espaciado" && (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-neutral-900">Espaciado</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>Espacio entre widgets</span>
                          <span className="tabular-nums">{slideLayoutUi.widgetGapRem.toFixed(2)} rem</span>
                        </div>
                        <Slider
                          value={[Math.round(slideLayoutUi.widgetGapRem * 100)]}
                          min={15}
                          max={220}
                          step={5}
                          onValueChange={([v]) =>
                            setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, widgetGapRem: v / 100 } : s)))
                          }
                        />
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>Ancho máx. (1 columna)</span>
                          <span className="tabular-nums">{slideLayoutUi.widgetMaxWidthPx}px</span>
                        </div>
                        <Slider
                          value={[slideLayoutUi.widgetMaxWidthPx]}
                          min={320}
                          max={960}
                          step={8}
                          onValueChange={([v]) =>
                            setSlides((prev) => prev.map((s, i) => (i === safeIndex ? { ...s, widgetMaxWidthPx: v } : s)))
                          }
                        />
                      </div>
                    </div>
                  )}

                  {rightPanelSection === "ordenar" && (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-neutral-900">Ordenar</p>
                      <div className="space-y-2">
                        {selectedWidgetIds.length > 1 ? (
                          <p className="text-[11px] text-neutral-600 leading-snug rounded-md border border-violet-100 bg-violet-50/80 px-2 py-1.5">
                            {selectedWidgetIds.length} seleccionados. Usa <kbd className="font-mono text-[10px]">Shift</kbd> + clic para añadir o
                            quitar. Las propiedades de estilo/contenido son del último elemento en el que hiciste clic.
                          </p>
                        ) : null}
                        {widgets.length > 0 ? (
                          <div className="space-y-1.5">
                            <span className="text-xs text-neutral-500">Orden en la diapositiva (arrastra)</span>
                            <p className="text-[10px] text-neutral-400 leading-snug">
                              Solo bloques principales. Dentro de «Columnas», clic derecho en el lienzo: Subir / Bajar posición.
                            </p>
                            <RootWidgetOrderStrip widgets={widgets} onReorder={reorderRootFromDrag} />
                          </div>
                        ) : null}
                        {selectedWidget ? (
                          <div className="flex gap-1 pt-1">
                            <Button type="button" variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={moveWidgetUp}>
                              <ChevronUp className="w-3.5 h-3.5 mr-0.5" />
                              Subir
                            </Button>
                            <Button type="button" variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={moveWidgetDown}>
                              <ChevronDown className="w-3.5 h-3.5 mr-0.5" />
                              Bajar
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {rightPanelSection === "estilo" && (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-neutral-900">Estilo</p>
                      {!selectedWidget ? (
                        <p className="text-xs text-neutral-500">Selecciona un widget en el lienzo.</p>
                      ) : (
                        <div className="space-y-3">
                          {(selectedWidget.type === "heading" ||
                            selectedWidget.type === "text" ||
                            selectedWidget.type === "bullets" ||
                            selectedWidget.type === "sticky" ||
                            selectedWidget.type === "chips") && (
                            <div className="space-y-1.5">
                              <span className="text-xs text-neutral-500">Alineación</span>
                              <div className="flex gap-1">
                                {[
                                  { k: "left", Icon: AlignLeft },
                                  { k: "center", Icon: AlignCenter },
                                  { k: "right", Icon: AlignRight },
                                  ...(selectedWidget.type !== "chips" ? [{ k: "justify", Icon: AlignJustify }] : []),
                                ].map(({ k, Icon }) => (
                                  <Button
                                    key={k}
                                    type="button"
                                    variant={selectedWidgetSize.textAlign === k ? "default" : "outline"}
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    onClick={() => patchSelectedWidget({ textAlign: k })}
                                    aria-label={k}
                                  >
                                    <Icon className="w-4 h-4" />
                                  </Button>
                                ))}
                              </div>
                              {selectedWidget.type !== "chips" && (
                                <div className="flex gap-1">
                                  {[
                                    { k: "start", Icon: AlignVerticalJustifyStart },
                                    { k: "center", Icon: AlignVerticalJustifyCenter },
                                    { k: "end", Icon: AlignVerticalJustifyEnd },
                                  ].map(({ k, Icon }) => (
                                    <Button
                                      key={k}
                                      type="button"
                                      variant={selectedWidgetSize.verticalAlign === k ? "default" : "outline"}
                                      size="icon"
                                      className="h-8 w-8 shrink-0"
                                      onClick={() => patchSelectedWidget({ verticalAlign: k })}
                                      aria-label={`vertical-${k}`}
                                    >
                                      <Icon className="w-4 h-4" />
                                    </Button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {selectedWidget.type === "emoji" && (
                            <div className="space-y-2 border-b border-neutral-100 pb-3">
                              <span className="text-xs text-neutral-500 block">Emoji seleccionado</span>
                              <div className="flex items-center gap-3">
                                <EmojiPickerPopover
                                  value={selectedWidget.text}
                                  onSelect={(e) => patchSelectedWidget({ text: e })}
                                >
                                  <Button type="button" variant="outline" className="h-14 w-14 text-4xl shrink-0">
                                    {selectedWidget.text || "🎉"}
                                  </Button>
                                </EmojiPickerPopover>
                                <div className="text-xs text-neutral-500 leading-relaxed">
                                  <p>Pulsa el emoji para</p>
                                  <p>seleccionar otro.</p>
                                </div>
                              </div>
                            </div>
                          )}
                          {widgetSupportsTextTypography(selectedWidget.type) && (
                            <div className="space-y-2 border-t border-neutral-100 pt-3">
                              <span className="text-xs text-neutral-500 block">{typographySizeLabel(selectedWidget.type)}</span>
                              <Slider
                                value={[Math.max(1, Math.min(5, Number(selectedWidgetSize.fontSizeLevel) || 3))]}
                                min={1}
                                max={5}
                                step={1}
                                onValueChange={([v]) => patchSelectedWidget({ fontSizeLevel: v })}
                              />
                              <div className="flex justify-between text-[10px] text-neutral-400 px-0.5 tabular-nums">
                                <span>{typographySizeSliderEnds(selectedWidget.type).left}</span>
                                <span>{typographySizeSliderEnds(selectedWidget.type).right}</span>
                              </div>
                              {selectedWidget.type !== "emoji" && (
                                <>
                                  <span className="text-xs text-neutral-500 block">{typographyColorLabel(selectedWidget.type)}</span>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="color"
                                      className="h-8 w-10 shrink-0 cursor-pointer rounded border border-neutral-200 bg-white p-0.5"
                                      aria-label="Color del texto"
                                      value={
                                        /^#[0-9a-fA-F]{6}$/.test(String(selectedWidget.textColor || "").trim())
                                          ? String(selectedWidget.textColor).trim()
                                          : onboardingTextColorFromWidget({}, typoPresetForWidgetType(selectedWidget.type))
                                      }
                                      onChange={(e) => patchSelectedWidget({ textColor: e.target.value })}
                                    />
                                    <Input
                                      className="h-8 text-xs font-mono flex-1"
                                      value={selectedWidget.textColor || ""}
                                      onChange={(e) => patchSelectedWidget({ textColor: e.target.value })}
                                      placeholder={onboardingTextColorFromWidget({}, typoPresetForWidgetType(selectedWidget.type))}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          <Separator className="my-1 bg-neutral-200" />
                          <div className="space-y-2 pt-1">
                            <span className="text-xs font-medium text-neutral-700 block">Padding del bloque</span>
                            <p className="text-[10px] text-neutral-400 leading-snug">
                              Márgenes internos del widget (cada paso = 0,125 rem; máx. 3 rem por lado).
                            </p>
                            {[
                              { key: "paddingTopPx", label: "Arriba" },
                              { key: "paddingRightPx", label: "Derecha" },
                              { key: "paddingBottomPx", label: "Abajo" },
                              { key: "paddingLeftPx", label: "Izquierda" },
                            ].map(({ key, label }) => (
                              <div key={key} className="space-y-1">
                                <div className="flex justify-between text-xs text-neutral-500">
                                  <span>{label}</span>
                                  <span className="tabular-nums">
                                    {(clampWidgetPaddingUnit(selectedWidget[key]) * 0.125).toFixed(2)} rem
                                  </span>
                                </div>
                                <Slider
                                  value={[clampWidgetPaddingUnit(selectedWidget[key])]}
                                  min={0}
                                  max={24}
                                  step={1}
                                  onValueChange={([v]) => patchSelectedWidget({ [key]: v })}
                                />
                              </div>
                            ))}
                          </div>
                          <div className="space-y-2 pt-1 border-t border-neutral-100">
                            <span className="text-xs font-medium text-neutral-700 block">Margen del bloque</span>
                            <p className="text-[10px] text-neutral-400 leading-snug">
                              Separación respecto al flujo del lienzo; mismos pasos que el padding (±3 rem).
                            </p>
                            {[
                              { key: "marginTopPx", label: "Arriba" },
                              { key: "marginRightPx", label: "Derecha" },
                              { key: "marginBottomPx", label: "Abajo" },
                              { key: "marginLeftPx", label: "Izquierda" },
                            ].map(({ key, label }) => (
                              <div key={key} className="space-y-1">
                                <div className="flex justify-between text-xs text-neutral-500">
                                  <span>{label}</span>
                                  <span className="tabular-nums">
                                    {(clampWidgetMarginUnit(selectedWidget[key]) * 0.125).toFixed(2)} rem
                                  </span>
                                </div>
                                <Slider
                                  value={[clampWidgetMarginUnit(selectedWidget[key])]}
                                  min={-24}
                                  max={24}
                                  step={1}
                                  onValueChange={([v]) => patchSelectedWidget({ [key]: v })}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {rightPanelSection === "contenido" && (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-neutral-900">Contenido</p>
                      {!selectedWidget ? (
                        <p className="text-xs text-neutral-500">Selecciona un widget en el lienzo.</p>
                      ) : (
                        <div className="space-y-3">
                          {String(selectedWidget.type || "").startsWith("embed_") ? (
                            <div>
                              <label className="text-xs text-neutral-500 mb-1 block">URL del contenido</label>
                              <textarea
                                className="w-full min-h-[72px] text-xs rounded-md border border-neutral-200 p-2 resize-y focus:outline-none focus:ring-2 focus:ring-violet-400 font-mono"
                                value={selectedWidget.text || ""}
                                onChange={(e) => patchSelectedWidget({ text: e.target.value })}
                                placeholder="URL de vídeo, publicación o perfil (TikTok / Instagram)"
                              />
                            </div>
                          ) : null}
                          {selectedWidget.type === "image" ? (
                            <div className="space-y-2">
                              <label className="text-xs text-neutral-500">Imagen (URL o archivo)</label>
                              <Input
                                className="h-8 text-xs font-mono"
                                value={selectedWidget.imageSrc || ""}
                                onChange={(e) => patchSelectedWidget({ imageSrc: e.target.value })}
                                placeholder="https://… o data:image/…"
                              />
                              <label className="inline-flex cursor-pointer items-center justify-center rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-xs hover:bg-neutral-50 w-full">
                                Subir imagen
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="sr-only"
                                  onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = "";
                                    if (!f) return;
                                    try {
                                      const u = await compressImageFile(f);
                                      patchSelectedWidget({ imageSrc: u });
                                    } catch (_) {
                                      toast.error("No se pudo leer la imagen");
                                    }
                                  }}
                                />
                              </label>
                              <div>
                                <label className="text-xs text-neutral-500 mb-1 block">Leyenda (opcional)</label>
                                <Input
                                  className="h-8 text-xs"
                                  value={selectedWidget.text || ""}
                                  onChange={(e) => patchSelectedWidget({ text: e.target.value })}
                                  placeholder="Pie de foto"
                                />
                              </div>
                              <div>
                                <span className="text-xs text-neutral-500 mb-1 block">Forma del marco</span>
                                <div className="flex flex-col gap-1">
                                  {[
                                    { k: "rect", label: "Rectangular (marco libre)" },
                                    { k: "square", label: "Cuadrada (1:1)" },
                                    { k: "circle", label: "Circular" },
                                  ].map(({ k, label }) => (
                                    <Button
                                      key={k}
                                      type="button"
                                      size="sm"
                                      variant={normalizedImageBoxShape(selectedWidget.imageBoxShape) === k ? "default" : "outline"}
                                      className="h-8 justify-start text-xs"
                                      onClick={() => {
                                        const wf = Math.max(0.1, Math.min(1, Number(selectedWidget.w) || 1));
                                        if (k === "square" || k === "circle") {
                                          patchSelectedWidget({ imageBoxShape: k, h: imageSquareNormalizedHFromW(wf) });
                                        } else {
                                          patchSelectedWidget({ imageBoxShape: "rect" });
                                        }
                                      }}
                                    >
                                      {label}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={!!selectedWidget.lockAspectRatio}
                                  onChange={(e) => patchSelectedWidget({ lockAspectRatio: e.target.checked })}
                                  className="rounded"
                                />
                                Mantener proporción de la imagen (no deformar al cambiar el marco)
                              </label>
                              {normalizedImageBoxShape(selectedWidget.imageBoxShape) !== "circle" ? (
                                <div className="space-y-1">
                                  <div className="flex justify-between text-xs text-neutral-500">
                                    <span>Redondeo bordes</span>
                                    <span>{Math.max(0, Math.min(50, Number(selectedWidget.borderRadius) || 0))}</span>
                                  </div>
                                  <Slider
                                    value={[Math.max(0, Math.min(50, Number(selectedWidget.borderRadius) || 0))]}
                                    min={0}
                                    max={50}
                                    step={1}
                                    onValueChange={([v]) => patchSelectedWidget({ borderRadius: v })}
                                  />
                                </div>
                              ) : (
                                <p className="text-[10px] text-neutral-400 leading-snug">En forma circular el recorte es siempre circular.</p>
                              )}
                              <div className="space-y-2 border-t border-neutral-100 pt-2">
                                <span className="text-xs text-neutral-500 block">Borde de la imagen</span>
                                <div className="flex gap-2 items-center">
                                  <input
                                    type="color"
                                    className="h-8 w-10 shrink-0 cursor-pointer rounded border border-neutral-200 bg-white p-0.5"
                                    aria-label="Color del borde"
                                    value={
                                      /^#[0-9a-fA-F]{6}$/.test(String(selectedWidget.imageBorderColor || "").trim())
                                        ? String(selectedWidget.imageBorderColor).trim()
                                        : "#d4d4d4"
                                    }
                                    onChange={(e) => patchSelectedWidget({ imageBorderColor: e.target.value })}
                                  />
                                  <Input
                                    className="h-8 text-xs font-mono flex-1"
                                    value={selectedWidget.imageBorderColor || ""}
                                    onChange={(e) => patchSelectedWidget({ imageBorderColor: e.target.value })}
                                    placeholder="#d4d4d4"
                                  />
                                </div>
                                <div className="flex justify-between text-xs text-neutral-500">
                                  <span>Grosor borde</span>
                                  <span className="tabular-nums">{clampImageBorderWidthPx(selectedWidget.imageBorderWidthPx)} px</span>
                                </div>
                                <Slider
                                  value={[clampImageBorderWidthPx(selectedWidget.imageBorderWidthPx)]}
                                  min={0}
                                  max={16}
                                  step={1}
                                  onValueChange={([v]) => patchSelectedWidget({ imageBorderWidthPx: v })}
                                />
                              </div>
                              <div className="space-y-2 border-t border-neutral-100 pt-2">
                                <span className="text-xs text-neutral-500 block">Sombra paralela</span>
                                <div className="flex justify-between text-xs text-neutral-500">
                                  <span>Desplazamiento</span>
                                  <span className="tabular-nums">{clampImageShadowOffsetPx(selectedWidget.imageShadowOffsetPx)} px</span>
                                </div>
                                <Slider
                                  value={[clampImageShadowOffsetPx(selectedWidget.imageShadowOffsetPx)]}
                                  min={0}
                                  max={24}
                                  step={1}
                                  onValueChange={([v]) => patchSelectedWidget({ imageShadowOffsetPx: v })}
                                />
                                <Input
                                  className="h-8 text-xs font-mono"
                                  value={selectedWidget.imageShadowColor || ""}
                                  onChange={(e) => patchSelectedWidget({ imageShadowColor: e.target.value })}
                                  placeholder="rgba(15, 23, 42, 0.22)"
                                />
                                <p className="text-[10px] text-neutral-400 leading-snug">0 px desactiva la sombra. Color en hex o rgba.</p>
                              </div>
                            </div>
                          ) : null}
                          {selectedWidget.type === "chips" ? (
                            <div>
                              <label className="text-xs text-neutral-500 mb-1 block">Chips clicables</label>
                              <OnboardingChipsEditor text={selectedWidget.text || ""} onText={(t) => patchSelectedWidget({ text: t })} />
                            </div>
                          ) : null}
                          {selectedWidget.type === "columns_row" ? (
                            <div className="space-y-2">
                              <label className="text-xs text-neutral-500">Columnas dentro de la fila</label>
                              <div className="flex gap-1">
                                {[1, 2, 3].map((n) => {
                                  const cur = parseColumnsRow(selectedWidget.text || "{}").columnCount;
                                  return (
                                    <Button
                                      key={n}
                                      type="button"
                                      size="sm"
                                      variant={cur === n ? "default" : "outline"}
                                      className="h-8 flex-1 text-xs"
                                      onClick={() => patchSelectedWidget({ text: setColumnsRowColumnCount(selectedWidget.text || "{}", n) })}
                                    >
                                      {n}
                                    </Button>
                                  );
                                })}
                              </div>
                              <p className="text-[10px] text-neutral-400 leading-snug">
                                Con la fila seleccionada, la barra izquierda inserta en la columna 1. Aquí puedes elegir columna.
                              </p>
                              <span className="text-xs text-neutral-500">Añadir párrafo en…</span>
                              <div className="flex flex-col gap-1">
                                {parseColumnsRow(selectedWidget.text || "{}").cells.map((_, colIdx) => (
                                  <Button
                                    key={colIdx}
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-8 text-xs justify-start"
                                    onClick={() => {
                                      const nw = createWidget("text");
                                      updateCurrentWidgets((list) => {
                                        const row = findWidgetById(list, primarySelectedId);
                                        if (!row || row.type !== "columns_row") return list;
                                        return updateWidgetById(list, primarySelectedId, {
                                          text: appendWidgetToColumn(row.text, colIdx, nw),
                                        });
                                      });
                                      setSelectedWidgetIds([nw.id]);
                                    }}
                                  >
                                    Columna {colIdx + 1}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          ) : null}
                          {selectedWidget.type !== "divider" &&
                          selectedWidget.type !== "shape" &&
                          selectedWidget.type !== "heading" &&
                          selectedWidget.type !== "text" &&
                          selectedWidget.type !== "bullets" &&
                          selectedWidget.type !== "sticky" &&
                          !String(selectedWidget.type || "").startsWith("embed_") &&
                          selectedWidget.type !== "image" &&
                          selectedWidget.type !== "chips" &&
                          selectedWidget.type !== "columns_row" ? (
                            <div>
                              <label className="text-xs text-neutral-500 mb-1 block">Texto</label>
                              <textarea
                                className="w-full min-h-[120px] text-sm rounded-md border border-neutral-200 p-2 resize-y focus:outline-none focus:ring-2 focus:ring-violet-400"
                                value={selectedWidget.text || ""}
                                onChange={(e) => patchSelectedWidget({ text: e.target.value })}
                              />
                            </div>
                          ) : null}
                          {selectedWidget.type === "divider" ? (
                            <div className="space-y-2">
                              <label className="text-xs text-neutral-500 block">Estilo de línea</label>
                              <div className="flex gap-1">
                                {[
                                  { k: "solid", label: "—" },
                                  { k: "dashed", label: "- -" },
                                  { k: "dotted", label: "···" },
                                ].map(({ k, label }) => (
                                  <Button
                                    key={k}
                                    type="button"
                                    variant={(selectedWidget.lineStyle || "solid") === k ? "default" : "outline"}
                                    size="sm"
                                    className="flex-1 h-8 text-[11px] font-mono tracking-widest"
                                    onClick={() => patchSelectedWidget({ lineStyle: k })}
                                  >
                                    {label}
                                  </Button>
                                ))}
                              </div>
                              <label className="text-xs text-neutral-500 block">Color</label>
                              <input
                                type="color"
                                className="h-9 w-full rounded-md border cursor-pointer"
                                value={/^#[0-9a-fA-F]{6}$/.test(selectedWidget.accent || "") ? selectedWidget.accent : "#d4d4d4"}
                                onChange={(e) => patchSelectedWidget({ accent: e.target.value })}
                              />
                            </div>
                          ) : null}
                          {selectedWidget.type === "shape" ? (
                            <div>
                              <label className="text-xs text-neutral-500 mb-1 block">Color</label>
                              <input
                                type="color"
                                className="h-9 w-full rounded-md border cursor-pointer"
                                value={/^#[0-9a-fA-F]{6}$/.test(selectedWidget.accent || "") ? selectedWidget.accent : "#eef2ff"}
                                onChange={(e) => patchSelectedWidget({ accent: e.target.value })}
                              />
                            </div>
                          ) : null}
                          <Button type="button" variant="destructive" size="sm" className="w-full gap-2" onClick={deleteSelectedWidget}>
                            <Trash2 className="w-4 h-4" />
                            Eliminar widget
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <nav
                className="flex w-12 shrink-0 flex-col items-center gap-0.5 border-l border-neutral-100 bg-white py-2"
                aria-label="Secciones del panel derecho"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant={rightPanelSection === "lienzo" ? "secondary" : "ghost"}
                      className="h-9 w-9 shrink-0 rounded-lg text-neutral-600 hover:text-violet-700 hover:bg-violet-50"
                      aria-label="Lienzo"
                      aria-pressed={rightPanelSection === "lienzo"}
                      onClick={() => setRightPanelSection((s) => (s === "lienzo" ? null : "lienzo"))}
                    >
                      <LayoutTemplate className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Lienzo</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant={rightPanelSection === "espaciado" ? "secondary" : "ghost"}
                      className="h-9 w-9 shrink-0 rounded-lg text-neutral-600 hover:text-violet-700 hover:bg-violet-50"
                      aria-label="Espaciado"
                      aria-pressed={rightPanelSection === "espaciado"}
                      onClick={() => setRightPanelSection((s) => (s === "espaciado" ? null : "espaciado"))}
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Espaciado</TooltipContent>
                </Tooltip>
                <Separator className="my-1 w-7 shrink-0 bg-neutral-200" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant={rightPanelSection === "ordenar" ? "secondary" : "ghost"}
                      className="h-9 w-9 shrink-0 rounded-lg text-neutral-600 hover:text-violet-700 hover:bg-violet-50"
                      aria-label="Ordenar"
                      aria-pressed={rightPanelSection === "ordenar"}
                      onClick={() => setRightPanelSection((s) => (s === "ordenar" ? null : "ordenar"))}
                    >
                      <ArrowDownUp className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Ordenar</TooltipContent>
                </Tooltip>
                <Separator className="my-1 w-7 shrink-0 bg-neutral-200" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant={rightPanelSection === "estilo" ? "secondary" : "ghost"}
                      className="h-9 w-9 shrink-0 rounded-lg text-neutral-600 hover:text-violet-700 hover:bg-violet-50"
                      aria-label="Estilo"
                      aria-pressed={rightPanelSection === "estilo"}
                      onClick={() => setRightPanelSection((s) => (s === "estilo" ? null : "estilo"))}
                    >
                      <Palette className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Estilo</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant={rightPanelSection === "contenido" ? "secondary" : "ghost"}
                      className="h-9 w-9 shrink-0 rounded-lg text-neutral-600 hover:text-violet-700 hover:bg-violet-50"
                      aria-label="Contenido"
                      aria-pressed={rightPanelSection === "contenido"}
                      onClick={() => setRightPanelSection((s) => (s === "contenido" ? null : "contenido"))}
                    >
                      <Boxes className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Contenido</TooltipContent>
                </Tooltip>
              </nav>
            </aside>

          </div>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className={cn(
            "z-[200] flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none border-0 bg-neutral-950 p-0 text-neutral-100 shadow-none",
            "!inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 duration-200",
            "[&>button:last-child]:hidden"
          )}
        >
          {/* Barra superior: título | dispositivo (centro) | cerrar */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-center border-b border-neutral-800 px-3 py-2.5 shrink-0">
            <span className="text-sm font-medium truncate">Vista previa</span>
            <div className="flex items-center gap-1 justify-self-center">
              <Button
                type="button"
                size="sm"
                variant={previewViewport === "desktop" ? "secondary" : "ghost"}
                className="h-8 gap-1.5 text-xs"
                onClick={() => setPreviewViewport("desktop")}
              >
                <Monitor className="w-4 h-4" />
                Escritorio
              </Button>
              <Button
                type="button"
                size="sm"
                variant={previewViewport === "mobile" ? "secondary" : "ghost"}
                className="h-8 gap-1.5 text-xs"
                onClick={() => setPreviewViewport("mobile")}
              >
                <Smartphone className="w-4 h-4" />
                Móvil
              </Button>
            </div>
            <div className="flex justify-end">
              <DialogClose asChild>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800" aria-label="Cerrar vista previa">
                  <X className="w-4 h-4" />
                </Button>
              </DialogClose>
            </div>
          </div>

          {/* Área de lienzo */}
          <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden bg-neutral-900/95 p-3 sm:p-6">
            {(() => {
              const pvSlide = slides[previewIndex] || null;
              const pvWidgets = pvSlide?.widgets || [];
              return (
                <div
                  className={cn(
                    "w-full shrink-0 min-h-0 bg-black",
                    previewViewport === "mobile"
                      ? "max-w-[min(calc(100vh*9/16-2rem),390px)] [aspect-ratio:9/16]"
                      : "max-w-[min(96vw,960px)] [aspect-ratio:16/9]"
                  )}
                >
                  <OnboardingCanvasStage
                    widgets={pvWidgets}
                    layoutSlide={pvSlide}
                    background={pvSlide?.background || "#ffffff"}
                    readOnly
                    animated
                    fullscreen
                    className="h-full w-full min-h-0"
                    previewDevice={previewViewport === "mobile" ? "responsive" : undefined}
                  />
                </div>
              );
            })()}

            {/* Botones de navegación entre diapositivas */}
            {slides.length > 1 && (
              <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3">
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-10 w-10 rounded-full bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 border border-white/15 disabled:opacity-30"
                  disabled={previewIndex <= 0}
                  onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                  aria-label="Diapositiva anterior"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <span className="text-xs tabular-nums text-white/70 select-none min-w-[3rem] text-center">
                  {previewIndex + 1} / {slides.length}
                </span>
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-10 w-10 rounded-full bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 border border-white/15 disabled:opacity-30"
                  disabled={previewIndex >= slides.length - 1}
                  onClick={() => setPreviewIndex((i) => Math.min(slides.length - 1, i + 1))}
                  aria-label="Diapositiva siguiente"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
